import { useLayoutEffect } from "react";

// During playback, a shape colorizes once progress passes this ratio
// into its span (0 = leading edge, 1 = trailing edge)
const PLAYED_ACTIVATION_RATIO = 0.2;

/** Extract the viewBox width from the active gemscape SVG */
const getViewBoxWidth = container => {
  const svg = container.querySelector("svg.gemscape-active");
  if (!svg) return null;
  const vb = svg.getAttribute("viewBox");
  return vb ? parseFloat(vb.split(" ")[2]) : 100;
};

/**
 * usePlaybackProgress
 *
 * Drives the base-layer progress clip rect and shape-played class toggling.
 *
 * Playing mode: rect width driven by percentagePlayed, shapes toggle shape-played.
 * Hover mode: parent calls the returned updateHoverRect / resetHoverRect callbacks
 * from its own mouse handlers — no native listeners are added here.
 */
export function usePlaybackProgress({
  svgContainer,
  topShapesRef,
  isPlayingProgress,
  isActive,
  percentagePlayed,
  updateShapeClasses,
  markup
}) {
  // Playing progress — driven by percentagePlayed state changes.
  // useLayoutEffect prevents a flash: it runs synchronously before paint,
  // so shape-played classes are stamped before the browser renders grayscale.
  useLayoutEffect(() => {
    if (!isPlayingProgress) return;
    const container = svgContainer.current;
    if (!container) return;
    const rect = container.querySelector(".base-progress-rect");
    if (!rect) return;
    const vbWidth = getViewBoxWidth(container);
    if (vbWidth === null) return;

    const progressWidth = (percentagePlayed / 100) * vbWidth;
    rect.setAttribute("width", String(progressWidth));

    // Toggle top shapes based on progress vs shape center
    const progressFraction = percentagePlayed / 100;
    const shapes = topShapesRef.current;
    shapes.forEach(shape => {
      const start = parseFloat(shape.dataset.fractionStart);
      const end = parseFloat(shape.dataset.fractionEnd);
      const activationPoint = start + (end - start) * PLAYED_ACTIVATION_RATIO;
      if (progressFraction >= activationPoint) {
        shape.classList.add("shape-played");
      } else {
        shape.classList.remove("shape-played");
      }
    });

    // Drive shape-active from playback position.
    // Hero promotion is gated — deferred to the rAF pulse loop for
    // musically-synced activation on the next audio onset.
    updateShapeClasses(progressFraction);
  }, [
    percentagePlayed,
    isPlayingProgress,
    updateShapeClasses,
    markup,
    svgContainer,
    topShapesRef
  ]);

  // ── Hover rect callbacks ──────────────────────────────────────────
  const updateHoverRect = fraction => {
    const container = svgContainer.current;
    if (!container || !isActive || isPlayingProgress) return;
    const rect = container.querySelector(".base-progress-rect");
    if (!rect) return;
    const vbWidth = getViewBoxWidth(container);
    if (vbWidth === null) return;
    rect.setAttribute("width", String(fraction * vbWidth));

    // Mirror playing-mode reveal: top shapes left of the cursor colorize,
    // shapes to the right stay grayscale (driven by hover fraction).
    const shapes = topShapesRef.current;
    shapes.forEach(shape => {
      const start = parseFloat(shape.dataset.fractionStart);
      const end = parseFloat(shape.dataset.fractionEnd);
      const activationPoint = start + (end - start) * PLAYED_ACTIVATION_RATIO;
      if (fraction >= activationPoint) {
        shape.classList.add("shape-played");
      } else {
        shape.classList.remove("shape-played");
      }
    });
  };

  const resetHoverRect = () => {
    const container = svgContainer.current;
    if (!container || isPlayingProgress) return;
    const rect = container.querySelector(".base-progress-rect");
    if (rect) rect.setAttribute("width", "0");

    // Clear hover-driven reveal so shapes return to grayscale rest state.
    topShapesRef.current.forEach(shape => {
      shape.classList.remove("shape-played");
    });
  };

  return { updateHoverRect, resetHoverRect };
}
